import React from "react";
import { useTranslation } from "react-i18next";
import DateCurrentInput from "../inputs/DateCurrentInput";
import SpreadsheetItemInput from "../select/SpreadsheetItemInput";
import SpreadsheetWorkOrderDocumentInput from "../select/SpreadsheetWorkOrderDocumentInput";
import { getCachedUnitMeasurmentCode } from "../select/UnitMeasurmentSelect";
import { useItemCache } from "../context/ItemCacheContext";
import { parseDecimalInput, formatQtyForDisplay } from "../utils/decimalInput";
import OSCard, { OS_TH_CLS, OS_READONLY_CLS, OS_PLAIN_DISABLED_CLS } from "./OSCard";
import { allowedFinishedYieldOf, allowedFinishedWasteOf } from "./OSDetailHeader";

interface FinishedProductCardProps {
  mode: "os" | "rn";
  selectedItemId: string;
  onItemChange: (value: string) => void;
  batch?: number;
  onBatchChange: (value: number) => void;
  batchUnit?: string | null;
  batchUnitCode?: string;
  isEditable: boolean;
  showValidationErrors?: boolean;
  // mode="os"
  masterItem?: { ident?: string; name?: string } | null;
  selectedWorkOrderDocumentId?: string;
  onWorkOrderDocumentChange?: (value: string) => void;
  // mode="rn"
  selectedItemCode?: string;
  selectedItemName?: string;
  workOrderNumber?: string;
  workOrderDate?: string;
  productionPlanNumber?: string;
  plannedStartDateDisplay?: string;
  productionDateDisplay?: string;
  serialNumber?: string;
  expiryDate?: string;
  onFinishedProductChange?: (patch: { serialNumber?: string; expiryDate?: string }, markDirty?: boolean) => void;
  finishedProductLocked?: boolean;
  // Подпроцес прием на готовиот производ — изведено од последниот ред во Меѓу/Полупроизвод
  // (deriveFinishedReceiptSubprocessId). Read-only приказ, не се внесува и не се снима.
  receiptSubprocessName?: string;
  receivedQty?: string;
  receivedQtyConfirmed?: boolean;
  receivedQtyEditable?: boolean;
  onReceivedQtyChange?: (value: string) => void;
  // „Шифра на Тех. Пос." и Верзија се прикажуваат само во картичката „Податоци" (DetailDataCard) —
  // не се дуплираат тука.
}

// Canonical blue focus accent (CLAUDE.md hard rule — the yellow variant these carried is the
// known accent bug, not a style choice).
const cellInputCls =
  "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50";
const actualInputCls =
  `w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_CLS}`;

// `width`, not `minWidth` — заедно со `table-fixed` подолу. Оваа табела и „Меѓу/Полупроизвод"
// стојат една врз друга и носат ИДЕНТИЧЕН низ ширини, за колона N горе да се поклопи со колона
// N долу. Со auto layout тоа не се случува: minWidth е само под, па секоја табела си го дели
// вишокот простор според сопствената содржина („Количина споредна ЕМ" наспроти „Шифра на Тех.
// Пос.") и колоните се разминуваат. Ако менуваш ширина овде, смени ја и во OSSubprocessItemsTable.
const HeaderTH: React.FC<{ w: number; title?: string; children: React.ReactNode }> = ({ w, title, children }) => (
  <th
    className={OS_TH_CLS}
    style={{ width: w }}
    title={title}
  >
    {children}
  </th>
);

const FinishedProductCard: React.FC<FinishedProductCardProps> = ({
  mode,
  selectedItemId,
  onItemChange,
  batch,
  onBatchChange,
  batchUnit,
  batchUnitCode,
  isEditable,
  showValidationErrors = false,
  masterItem,
  selectedWorkOrderDocumentId,
  onWorkOrderDocumentChange,
  selectedItemCode,
  selectedItemName,
  workOrderNumber,
  workOrderDate,
  productionPlanNumber,
  plannedStartDateDisplay,
  productionDateDisplay,
  serialNumber,
  expiryDate,
  onFinishedProductChange,
  finishedProductLocked = false,
  receiptSubprocessName,
  receivedQty,
  receivedQtyConfirmed = false,
  receivedQtyEditable = false,
  onReceivedQtyChange,
}) => {
  const { t } = useTranslation();
  const { getItem, fetchItemById } = useItemCache();
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!selectedItemId) {
      setSelectedItem(null);
      return () => { cancelled = true; };
    }
    const cached = getItem(selectedItemId);
    if (cached) setSelectedItem(cached);
    fetchItemById(selectedItemId).then((item) => {
      if (!cancelled && item) setSelectedItem(item);
    });
    return () => { cancelled = true; };
  }, [selectedItemId, getItem, fetchItemById]);
  // Количина на серија се внесува и се прикажува со ЗАПИРКА (МК децимален знак).
  // Затоа полето е `type="text"`, НЕ `type="number"` — `number` секогаш форматира со
  // точка без оглед на локалот и одбива вредност со запирка како невалидна.
  // `batchDraft` ги држи суровите тастатурни удари додека се куца (инаку „1," веднаш би
  // се парсирало во 1 и децималата не би можела да се внесе); на blur се враќа на
  // канонскиот формат од `formatQtyForDisplay`.
  const [batchDraft, setBatchDraft] = React.useState<string | null>(null);
  const finishedAllowedYield = allowedFinishedYieldOf(batch, selectedItem || masterItem);
  const finishedAllowedWaste = allowedFinishedWasteOf(selectedItem || masterItem);

  const displayCode = mode === "os" ? masterItem?.ident || undefined : selectedItemCode;
  const displayName = mode === "os" ? masterItem?.name || undefined : selectedItemName;

  return (
    <OSCard title="Готов Производ">
      <div className="overflow-x-auto">
        <table className="border-collapse w-full table-fixed">
          <thead>
            <tr>
              <HeaderTH w={100}>{t("tableHeaders.receiptSubprocess")}</HeaderTH>
              <HeaderTH w={mode === "rn" ? 110 : 100}>
                {mode === "os" ? "Тип на РН" : t("tableHeaders.workOrderNumber", "Бр. на РН")}
              </HeaderTH>
              {/* РН носи 7 колони повеќе — кратки наслови (целото име во tooltip) и потесни колони,
                  за да собере без лизгање покрај Податоци. */}
              {mode === "rn" && <HeaderTH w={80} title={t("workOrder.workOrderDate")}>Дат. РН</HeaderTH>}
              {mode === "rn" && <HeaderTH w={80} title={t("workOrder.productionPlanNumber")}>План пр.</HeaderTH>}
              {mode === "rn" && <HeaderTH w={80} title={t("workOrder.plannedStartDate")}>План. поч.</HeaderTH>}
              {mode === "rn" && <HeaderTH w={80} title={t("os.materialno.productionDate")}>Дат. произв.</HeaderTH>}
              {mode === "rn" && <HeaderTH w={80}>{t("os.materialno.priemColLot")}</HeaderTH>}
              {mode === "rn" && <HeaderTH w={85} title={t("os.materialno.priemColExpiry")}>Рок</HeaderTH>}
              <HeaderTH w={mode === "rn" ? 100 : 120}>{t("tableHeaders.code")}</HeaderTH>
              <HeaderTH w={mode === "rn" ? 160 : 400}>{t("tableHeaders.name")}</HeaderTH>
              <HeaderTH w={58} title={t("ingredients.fields.batchQuantity", "Количина на серија")}>
                {t("ingredients.fields.batchQuantityShort", "Кол. серија")}
              </HeaderTH>
              {mode === "rn" && <HeaderTH w={70} title={t("os.materialno.priemColReceivedQty")}>Прим. кол.</HeaderTH>}
              <HeaderTH w={36}>ЕМ</HeaderTH>
              {mode === "rn"
                ? <HeaderTH w={60} title="Дозволен принос (ЕМ)">Дозв. прин.<br />(ЕМ)</HeaderTH>
                : <HeaderTH w={100}>Дозволен принос<br />(ЕМ)</HeaderTH>}
              {/* Експлицитен прелом: заглавието мора да е точно на ДВА реда, а не да зависи од
                  тоа каде ќе го скрши браузерот на дадената ширина. */}
              {mode === "rn"
                ? <HeaderTH w={55} title="Дозволен отпад (%)">Дозв. отп.<br />(%)</HeaderTH>
                : <HeaderTH w={90}>Дозволен отпад<br />(%)</HeaderTH>}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 h-[26px] px-1 py-0.5 text-center text-[11px] text-gray-700">
                {receiptSubprocessName || ""}
              </td>
              {mode === "os" ? (
                <td className="border border-gray-300 h-[26px] px-0.5 py-0.5 align-top">
                  {/* Read-only: derived from the item's tercialClass.workOrderType in OS.tsx
                      (handleSelectedItemChange), no longer a manual choice. */}
                  <SpreadsheetWorkOrderDocumentInput
                    value={selectedWorkOrderDocumentId || ""}
                    onChange={onWorkOrderDocumentChange!}
                    disabled={true}
                  />
                </td>
              ) : (
                <td
                  className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700 whitespace-nowrap tabular-nums bg-gray-50"
                  title={workOrderNumber || undefined}
                >
                  {/* Plain 11px text like the other cells — a disabled <input> rendered 13px and cut the number. */}
                  {workOrderNumber || "--"}
                </td>
              )}
              {mode === "rn" && (
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                  <input type="text" value={workOrderDate || ""} readOnly disabled className={`${cellInputCls} ${OS_READONLY_CLS}`} />
                </td>
              )}
              {mode === "rn" && <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top"><input type="text" value={productionPlanNumber || ""} readOnly disabled className={`${cellInputCls} ${OS_READONLY_CLS}`} /></td>}
              {mode === "rn" && <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top"><input type="text" value={plannedStartDateDisplay || ""} readOnly disabled className={`${cellInputCls} ${OS_READONLY_CLS}`} /></td>}
              {mode === "rn" && (
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                  <input type="text" value={productionDateDisplay || ""} readOnly disabled className={`${cellInputCls} ${OS_READONLY_CLS}`} />
                </td>
              )}
              {mode === "rn" && (
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                  <input className={actualInputCls} value={serialNumber || ""} disabled={finishedProductLocked} onChange={(e) => onFinishedProductChange?.({ serialNumber: e.target.value })} />
                </td>
              )}
              {mode === "rn" && (
                <td className="border border-gray-300 h-[26px] px-1 py-0.5 align-top">
                  <DateCurrentInput clearable={false} value={expiryDate || ""} onChange={(value) => onFinishedProductChange?.({ expiryDate: value })} disabled={finishedProductLocked} inputClassName={actualInputCls} />
                </td>
              )}
              <td className="border border-gray-300 h-[26px] px-0.5 py-0.5 align-top">
                <SpreadsheetItemInput
                  value={selectedItemId}
                  displayValue={displayCode}
                  displayBy="code"
                  enableValidation={true}
                  enableRef
                  onChange={onItemChange}
                  disabled={!isEditable}
                  size="tablePlain"
                  accountingTypeGroup="finished-product,semi-finished,intermediate"
                  {...(mode === "rn" ? { hasIngredient: true } : {})}
                />
                {showValidationErrors && !selectedItemId && (
                  <span className="text-xs text-red-500 mt-1 block">{t("validation.required")}</span>
                )}
              </td>
              <td className="border border-gray-300 h-[26px] px-0.5 py-0.5 align-top">
                <SpreadsheetItemInput
                  value={selectedItemId}
                  displayValue={displayName}
                  displayBy="name"
                  enableValidation={false}
                  enableRef
                  onChange={onItemChange}
                  disabled={!isEditable}
                  size="tablePlain"
                  accountingTypeGroup="finished-product,semi-finished,intermediate"
                  {...(mode === "rn" ? { hasIngredient: true } : {})}
                />
              </td>
              <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                <input
                  type="text"
                  inputMode="decimal"
                  value={batchDraft ?? formatQtyForDisplay(batch)}
                  onChange={(e) => {
                    setBatchDraft(e.target.value);
                    onBatchChange(parseDecimalInput(e.target.value) ?? 0);
                  }}
                  onBlur={() => setBatchDraft(null)}
                  disabled={!isEditable || mode === "rn"}
                  className={`${cellInputCls} text-right tabular-nums ${mode === "rn" ? OS_READONLY_CLS : OS_PLAIN_DISABLED_CLS}`}
                />
              </td>
              {mode === "rn" && (
                <td
                  className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px]${receivedQtyConfirmed ? " text-gray-700" : " text-gray-400"}`}
                  title={receivedQtyConfirmed ? undefined : t("os.materialno.priemNotConfirmed")}
                >
                  {receivedQtyEditable ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={t("os.materialno.priemColReceivedQty")}
                      className={`${actualInputCls} text-right`}
                      value={receivedQty || ""}
                      disabled={finishedProductLocked}
                      onChange={(event) => onReceivedQtyChange?.(event.target.value)}
                    />
                  ) : formatQtyForDisplay(parseDecimalInput(receivedQty))}
                </td>
              )}
              <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                <input
                  type="text"
                  value={batchUnitCode || getCachedUnitMeasurmentCode(batchUnit)}
                  readOnly
                  disabled
                  className={`${cellInputCls} text-center ${OS_READONLY_CLS}`}
                />
              </td>
              <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                <input
                  type="text"
                  value={finishedAllowedYield}
                  readOnly
                  disabled
                  className={`${cellInputCls} text-right tabular-nums ${OS_READONLY_CLS}`}
                />
              </td>
              <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 align-top">
                <input
                  type="text"
                  value={finishedAllowedWaste}
                  readOnly
                  disabled
                  className={`${cellInputCls} text-right tabular-nums ${OS_READONLY_CLS}`}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </OSCard>
  );
};

export default FinishedProductCard;
